/**
 * Phase 14.5 Step 2 — Degradation section (Phase 14).
 *
 * Profile select dropdown driven by the live
 * `GET /api/degradation-profiles` list. Surfaces the registry's
 * profile_version + rule count for operator orientation. "Create
 * new" link points to `/degradation-profiles/new` (the editor lands
 * in Step 3).
 *
 * Loads profile list on enable so we don't pull from the backend
 * unless the operator opts in.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Plus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import degradationProfiles from "@/api/degradationProfiles";

export default function DegradationSection({
    enabled = false,
    onToggleEnabled,
    value,
    onChange,
    registry,
}) {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const profileId = value?.profile_id || "";

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        setLoading(true);
        setErr(null);
        degradationProfiles
            .list({ page: 1, page_size: 100 })
            .then((body) => {
                if (cancelled) return;
                setProfiles(Array.isArray(body?.items) ? body.items : []);
            })
            .catch((e) => {
                if (!cancelled) setErr(e);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [enabled]);

    const setField = (k, v) => onChange?.({ ...(value || {}), [k]: v });

    const ruleCount = Array.isArray(registry?.rules) ? registry.rules.length : 0;
    const profileVersion = registry?.profile_version || null;

    return (
        <section
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
            data-testid="section-degradation"
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-start gap-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid="toggle-degradation"
                    aria-label="Enable degradation profile"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Layers className="size-4 text-ux-text-3" />
                        <span className="text-sm font-medium text-ux-text-1">Degradation profile</span>
                    </div>
                    <p className="text-xs text-ux-text-3">
                        Attach a post-engine degradation profile to a Geometry Sweep. The
                        engine runs unchanged; the pipeline produces a degraded observable
                        track + a modulated outcome via M2 rules.
                    </p>
                </div>
            </div>

            {enabled && (
                <div className="mt-4 space-y-3" data-testid="degradation-details">
                    {profileVersion && (
                        <p
                            className="text-xs text-ux-text-3"
                            data-testid="degradation-registry-version"
                        >
                            Registry profile_version{" "}
                            <code className="rounded bg-ux-surface-2/60 px-1">{profileVersion}</code>
                            {" — "}
                            {ruleCount} M2 modulation rules.
                        </p>
                    )}
                    <div>
                        <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            Select a profile
                        </Label>
                        {loading && (
                            <p className="text-xs text-ux-text-3" data-testid="degradation-profiles-loading">
                                Loading profiles…
                            </p>
                        )}
                        {err && (
                            <p className="text-xs text-red-300" data-testid="degradation-profiles-error">
                                Failed to load profiles.
                            </p>
                        )}
                        {!loading && !err && profiles.length === 0 && (
                            <p className="text-xs text-ux-text-3" data-testid="degradation-profiles-empty">
                                No profiles yet — create the first one.
                            </p>
                        )}
                        {!loading && !err && profiles.length > 0 && (
                            <Select
                                value={profileId || "none"}
                                onValueChange={(v) => setField("profile_id", v === "none" ? "" : v)}
                            >
                                <SelectTrigger data-testid="degradation-profile-select">
                                    <SelectValue placeholder="Select a profile" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— none —</SelectItem>
                                    {profiles.map((p) => (
                                        <SelectItem
                                            key={p.id}
                                            value={p.id}
                                            data-testid={`degradation-profile-option-${p.id}`}
                                        >
                                            {p.name || p.id}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                    <Link
                        to="/degradation-profiles/new"
                        className="inline-flex items-center gap-1 text-xs underline"
                        data-testid="degradation-create-new-link"
                    >
                        <Plus className="size-3" />
                        Create new degradation profile
                    </Link>
                </div>
            )}
        </section>
    );
}
